import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { GTAOPass } from "three/addons/postprocessing/GTAOPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { Pass, FullScreenQuad } from "three/addons/postprocessing/Pass.js";
import { CopyShader } from "three/addons/shaders/CopyShader.js";

// Quality is a rendering budget. It never changes gameplay, collision or timing.
export const QUALITY_PROFILES = Object.freeze({
  // ibl: image-based lighting from the sky. On weak GPUs it is the largest per-pixel cost
  // (SwiftShader at phone size: 14.5 → 32.5 fps without it), so the low tier lights with the
  // hemisphere alone, brightened to match: the "toy" look.
  low: Object.freeze({ post: false, ao: false, shadows: false, softShadows: false, shadowMap: 0, pixelRatio: 1, samples: 0, ibl: false }),
  medium: Object.freeze({ post: true, ao: false, shadows: true, softShadows: true, shadowMap: 1024, pixelRatio: 1.25, samples: 4, ibl: true }),
  high: Object.freeze({ post: true, ao: true, shadows: true, softShadows: true, shadowMap: 2048, pixelRatio: 1.75, samples: 4, ibl: true }),
});

export function resolveQuality(setting, width, software = false) {
  if (setting === "low" || setting === "high" || setting === "medium") return setting;
  // Software rasterizers (no GPU acceleration) cannot afford post-processing at interactive rates.
  if (software) return "low";
  return width < 760 ? "medium" : "high";
}

export function isSoftwareRenderer(renderer) {
  try {
    const gl = renderer.getContext();
    const debug = gl.getExtension("WEBGL_debug_renderer_info");
    const name = String(gl.getParameter(debug ? debug.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
    return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(name);
  } catch { return false; }
}

const GradeShader = {
  uniforms: { tDiffuse: { value: null }, vignette: { value: .32 }, saturation: { value: 1.06 }, lift: { value: .012 } },
  vertexShader: /* glsl */`varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse; uniform float vignette; uniform float saturation; uniform float lift;
    varying vec2 vUv;
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      float luma = dot(color.rgb, vec3(0.2126, 0.7152, 0.0722));
      color.rgb = mix(vec3(luma), color.rgb, saturation) + lift * (1.0 - color.rgb);
      vec2 d = (vUv - 0.5) * vec2(1.1, 1.0);
      color.rgb *= mix(1.0, smoothstep(0.95, 0.28, length(d) * 1.2), vignette);
      gl_FragColor = color;
    }`,
};

// Multisampling is paid once: the scene renders into a private MSAA target (with the stencil the
// x-ray silhouette needs), resolves a single time, and every later pass works on plain targets.
class SceneMSAAPass extends Pass {
  constructor(scene, camera, samples, onRendered) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.onRendered = onRendered;
    this.needsSwap = false;
    this.target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples, stencilBuffer: true });
    this.copy = new FullScreenQuad(new THREE.ShaderMaterial({ uniforms: THREE.UniformsUtils.clone(CopyShader.uniforms), vertexShader: CopyShader.vertexShader, fragmentShader: CopyShader.fragmentShader, depthTest: false, depthWrite: false }));
  }

  setSize(width, height) { this.target.setSize(width, height); }

  render(renderer, writeBuffer, readBuffer) {
    renderer.setRenderTarget(this.target);
    renderer.clear();
    renderer.shadowMap.needsUpdate = true;
    renderer.render(this.scene, this.camera);
    this.onRendered();
    this.copy.material.uniforms.tDiffuse.value = this.target.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : readBuffer);
    this.copy.render(renderer);
  }

  dispose() { this.target.dispose(); this.copy.material.dispose(); this.copy.dispose(); }
}

export class RenderPipeline {
  constructor(renderer, scene, camera) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.profile = QUALITY_PROFILES.low;
    this.level = "low";
    this.composer = null;
    this.width = 1;
    this.height = 1;
    this.bloomStrength = .32;
    // Scene cost (shadow map + main view) is tracked apart from the post-processing passes.
    this.sceneStats = { calls: 0, triangles: 0 };
    renderer.info.autoReset = false;
    // Shadows are refreshed explicitly once per frame by the scene render (see SceneMSAAPass).
    renderer.shadowMap.autoUpdate = false;
  }

  // antialias: false skips MSAA. On dense phone screens (device pixel ratio ≥ 2) a stair-step is
  // about two physical pixels wide, while MSAA was the largest cost of the phone tier
  // (SwiftShader at 390×844: +40–78% fps without it).
  configure(level, { antialias = true } = {}) {
    this.level = level;
    this.antialias = antialias;
    this.profile = QUALITY_PROFILES[level] || QUALITY_PROFILES.low;
    this.disposeComposer();
    const extensions = this.renderer.extensions;
    const floatTargets = extensions.has("EXT_color_buffer_float") || extensions.has("EXT_color_buffer_half_float");
    if (!this.profile.post || !this.renderer.capabilities.isWebGL2 || !floatTargets) return;
    // EffectComposer treats a supplied target's size as CSS pixels; setSize() below applies the pixel ratio.
    const size = this.renderer.getSize(new THREE.Vector2());
    const target = new THREE.WebGLRenderTarget(Math.max(1, size.x), Math.max(1, size.y), { type: THREE.HalfFloatType });
    const composer = new EffectComposer(this.renderer, target);
    composer.addPass(new SceneMSAAPass(this.scene, this.camera, antialias ? this.profile.samples : 0, () => this.recordScene()));
    if (this.profile.ao) {
      const ao = new GTAOPass(this.scene, this.camera, Math.max(1, size.x >> 1), Math.max(1, size.y >> 1));
      ao.updateGtaoMaterial({ radius: .55, distanceExponent: 1.4, thickness: 1.2, scale: 1.15, samples: 12 });
      ao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 6, rings: 2, samples: 12 });
      ao.blendIntensity = .85;
      // Labels, markers, beams and the sky do not occlude anything.
      const hide = ao._overrideVisibility.bind(ao);
      ao._overrideVisibility = () => {
        hide();
        this.scene.traverse(object => {
          if (!object.visible) return;
          const material = object.material;
          if (object.isSprite || object.userData.sky || material && !Array.isArray(material) && (material.transparent || !material.depthWrite)) {
            object.visible = false;
            ao._visibilityCache.push(object);
          }
        });
      };
      // Half-resolution AO keeps cost bounded on high-DPI displays.
      ao.setSize = (width, height) => GTAOPass.prototype.setSize.call(ao, Math.max(1, width >> 1), Math.max(1, height >> 1));
      composer.addPass(ao);
      this.ao = ao;
    }
    this.bloom = new UnrealBloomPass(new THREE.Vector2(size.x, size.y), this.bloomStrength, .55, .82);
    composer.addPass(this.bloom);
    composer.addPass(new OutputPass());
    this.grade = new ShaderPass(GradeShader);
    composer.addPass(this.grade);
    this.composer = composer;
    this.setSize(size.x, size.y);
  }

  setBloom(strength) {
    this.bloomStrength = strength;
    if (this.bloom) this.bloom.strength = strength;
  }

  setSize(width, height) {
    this.width = width;
    this.height = height;
    if (!this.composer) return;
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(width, height);
  }

  recordScene() {
    this.sceneStats.calls = this.renderer.info.render.calls;
    this.sceneStats.triangles = this.renderer.info.render.triangles;
  }

  render() {
    this.renderer.info.reset();
    if (this.composer) this.composer.render();
    else { this.renderer.shadowMap.needsUpdate = true; this.renderer.render(this.scene, this.camera); this.recordScene(); }
  }

  disposeComposer() {
    if (!this.composer) return;
    for (const pass of this.composer.passes) pass.dispose?.();
    this.composer.dispose();
    this.composer = this.bloom = this.ao = this.grade = null;
  }
}
